import {
  Assets,
  Texture,
  Rectangle,
  Text,
  Sprite,
  Container,
  Graphics,
  ColorMatrix,
} from "pixi.js";
import { ClientPokemon } from "./client-pokemon";
import { SheetSlice, makeSprite } from "@/utils/sprites/sheet-cut";
import { Direction } from "types/battle-engine/battle";

export class ClientDummyTarget extends ClientPokemon {
  override async load() {
    const texture = await Assets.load<Texture>(
      "/assets/misc/training-dummy.png",
    );
    const base = texture.source;
    base.scaleMode = "nearest";

    // Image has 3 rows. Max frames is 8.
    // Row 1: 4 frames
    // Row 2: 5 frames
    // Row 3: 8 frames

    // Assuming uniform grid based on max frames (8) and rows (3)
    const frameWidth = base.width / 8;
    const frameHeight = base.height / 3;

    const createFrames = (row: number, count: number) => {
      const frames: { texture: Texture; time: number }[] = [];
      for (let i = 0; i < count; i++) {
        frames.push({
          texture: new Texture({
            source: base,
            frame: new Rectangle(
              i * frameWidth,
              row * frameHeight,
              frameWidth,
              frameHeight,
            ),
          }),
          time: 100, // 100ms per frame
        });
      }
      return frames;
    };

    const hit1 = createFrames(0, 4);
    const hit2 = createFrames(1, 5);
    const hit3 = createFrames(2, 8);

    // Helper to create SheetSlice for all directions
    const createSlice = (
      frames: { texture: Texture; time: number }[],
      name: string,
    ): SheetSlice => {
      const byDir = {} as SheetSlice["byDir"];
      const dirs = [
        Direction.S,
        Direction.SE,
        Direction.E,
        Direction.NE,
        Direction.N,
        Direction.NW,
        Direction.W,
        Direction.SW,
      ];
      for (const dir of dirs) {
        byDir[dir] = frames;
      }
      return {
        byDir,
        cols: frames.length,
        meta: { name },
      };
    };

    this.animations["Hit1"] = createSlice(hit1, "Hit1");
    this.animations["Hit2"] = createSlice(hit2, "Hit2");
    this.animations["Hit3"] = createSlice(hit3, "Hit3");

    // Idle is just the first frame of Hit1
    const idleFrames = [hit1[0]!];
    this.animations["Idle"] = createSlice(idleFrames, "Idle");

    // Shadow - just use empty texture or simple shadow if possible
    // For now, we'll reuse the idle frame but tinted black and flattened for shadow
    const emptyTexture = Texture.EMPTY;
    const shadowFrames = [{ texture: emptyTexture, time: 100 }];
    const shadowSlice = createSlice(shadowFrames, "Idle");

    this.shadowAnimations["Idle"] = shadowSlice;
    this.shadowAnimations["Hit1"] = shadowSlice;
    this.shadowAnimations["Hit2"] = shadowSlice;
    this.shadowAnimations["Hit3"] = shadowSlice;

    // Set up initial sprite
    const animatedSprite = makeSprite(idleFrames);
    animatedSprite.anchor.set(0.5);
    animatedSprite.scale = 3; // Adjust scale as needed
    animatedSprite.play();
    this.animatedSprite = animatedSprite;

    const shadowAnimatedSprite = makeSprite(shadowFrames);
    shadowAnimatedSprite.anchor.set(0.5);
    this.animatedShadowSprite = shadowAnimatedSprite;

    this.container.addChild(shadowAnimatedSprite);
    this.container.addChild(animatedSprite);

    // Add quirky idle bounce
    this.app.ticker.add(() => {
      if (
        this.animatedSprite &&
        !this.animatedSprite.destroyed &&
        this.currentAnimName === "Idle"
      ) {
        const time = performance.now() / 200;
        const scaleBase = 4;
        const bounce = Math.sin(time) * 0.1; // +/- 0.1
        // Squash and stretch
        this.animatedSprite.scale.y = scaleBase + bounce;
        this.animatedSprite.scale.x = scaleBase - bounce * 0.5;
      } else if (this.animatedSprite && !this.animatedSprite.destroyed) {
        // Reset scale when not idle (e.g. during hit)
        this.animatedSprite.scale.set(4);
      }
    });

    // Setup UI
    if (!this.hideUI) {
      this.nameText = new Text({
        text: "Training Dummy",
        style: {
          fontFamily: "vck",
          fontSize: 12,
          fill: "0xffffff",
          align: "center",
          stroke: { color: 0x000000, width: 6, join: "round" },
        },
      });
      this.nameText.anchor.set(0.5, 1);
      this.container.addChild(this.nameText);

      if (!this.hideHPBar) {
        await this.createHPBar();
      }

      this.layoutUI(true);
    }

    this.setHP(this.hp.current);
    this.parent.addChild(this.container);
  }

  public override async takeDamage(
    rawAmount: number,
    skipAnimation: boolean = false,
  ): Promise<boolean> {
    if (!this.animatedSprite || this.animatedSprite.destroyed) return false;

    const amount = Math.round(rawAmount);

    // If damage is 0 (or less), treat as a miss/evade
    if (amount <= 0) {
      this.showFloatingDamage(0);
      return false;
    }

    // Allow DoT damage even if already hurting (just won't play animation)
    if (this.isHurting && !skipAnimation) return false;

    // Prevent concurrent flashes to avoid race conditions
    if (this.isFlashing) {
      // Still show damage and update state, just skip the flash
      // Call super.takeDamage from ClientPokemon's parent (Pokemon) logic?
      // No, we want to call ClientPokemon's logic but we are overriding it.
      // Actually we want to call Pokemon.takeDamage (the logic update) which is super.super?
      // No, ClientPokemon calls super.takeDamage() which is Pokemon.takeDamage().
      // Here super is ClientPokemon.
      // We can just call super.takeDamage() but that would trigger ClientPokemon's animation logic.
      // We want to avoid ClientPokemon's animation logic for the dummy specific parts.

      // Actually, ClientPokemon.takeDamage handles the logic update via `super.takeDamage(amount, skipAnimation)` (Pokemon.takeDamage).
      // We should replicate the structure or call super if we can control it.
      // But ClientPokemon.takeDamage has "Hurt" animation hardcoded.

      // Let's just copy the logic since it's cleaner than trying to hack super.
      // We need to call Pokemon.takeDamage. Since we can't call super.super, we rely on the fact that ClientPokemon extends Pokemon.
      // Wait, if I call super.takeDamage() it runs ClientPokemon.takeDamage().
      // ClientPokemon.takeDamage() does:
      // 1. Checks
      // 2. Sets isHurting
      // 3. Calls super.takeDamage() (Pokemon logic)
      // 4. Shows floating damage
      // 5. Plays animation (Hurt) and flash.

      // I want to replace step 5.
      // If I copy the code, I can't call super.takeDamage() (Pokemon logic) easily because `super` refers to ClientPokemon.
      // `Pokemon.prototype.takeDamage.call(this, ...)` could work but it's ugly.
      // But wait, `ClientPokemon` is just a wrapper for visuals. `Pokemon` class holds the data.
      // Let's look at `ClientPokemon.takeDamage` again.
      // It calls `super.takeDamage`.

      // Ideally I should have extracted `playHurtAnimation` in `ClientPokemon` to override it.
      // But I didn't.
      // I can just copy the logic and use `Pokemon.prototype.takeDamage.call(this, amount, skipAnimation)` if needed,
      // OR I can just call `super.takeDamage` and accept that it might try to play "Hurt" animation if I don't override it?
      // No, `ClientPokemon` checks `if (this.animations["Hurt"])`.
      // My dummy doesn't have "Hurt" animation, it has "Hit1", "Hit2", "Hit3".
      // So `ClientPokemon`'s `takeDamage` will skip the "Hurt" animation block!
      // It will still do the white flash loop.
      // So I can just override `takeDamage`, call `super.takeDamage`, and THEN play my random animation?
      // `ClientPokemon`'s `takeDamage` does the flash loop.
      // If I want to play my animation INSTEAD of "Hurt", I can rely on `this.animations["Hurt"]` being undefined.
      // BUT `ClientPokemon` plays the flash loop *after* the animation start.
      // And it waits for the flash loop.

      // If I use `super.takeDamage`, it will:
      // 1. Update HP (good)
      // 2. Show floating damage (good)
      // 3. Check for "Hurt" (false for dummy) -> skips animation start
      // 4. Do white flash loop (good)
      // 5. Restore animation to "Idle" (good)

      // The problem is I want to play "HitX" animation.
      // If I play it BEFORE calling super, super might override it?
      // `ClientPokemon` sets animation to "Idle" at the end.
      // If I play "HitX" (which is non-looping usually?), `ClientPokemon` might interrupt it or reset it.

      // Let's look at `ClientPokemon.takeDamage` again.
      /*
        if (this.animations["Hurt"]) {
          this.setAnimationOnce("Hurt", this.currentDir);
        }

        for (let i = 0; i < 2; i++) {
           // ... flash ...
           await this.sleep(30);
        }

        const restoreAnim = animationBeforeHurt === "Hurt" ? "Idle" : animationBeforeHurt;
        this.setAnimation([restoreAnim], this.currentDir, frameBeforeHurt);
      */

      // If I override `takeDamage`, I can fully control it.
      // But I need to call `Pokemon`'s `takeDamage` to update HP.
      // Since `ClientPokemon` extends `Pokemon`, I can call `super.takeDamage` (which is `ClientPokemon.takeDamage`).
      // But `ClientPokemon.takeDamage` does the visual stuff I want to customize.

      // The best way is to copy the implementation of `takeDamage` into `ClientDummyTarget` and call `Pokemon.prototype.takeDamage.call(this, ...)` for the logic part.
      // `Pokemon` is imported in `ClientPokemon`. I need to import `Pokemon` here too?
      // `ClientPokemon` extends `Pokemon`.
      // I can import `Pokemon` class to use prototype call.
    }

    if (!skipAnimation) {
      this.isHurting = true;
      this.events.emit("hurt", true);
    }

    // Call Pokemon logic (skip ClientPokemon logic)
    // We need to access the base Pokemon class method.
    // Since we can't easily access super.super, we use the prototype of the grandparent.
    // But wait, ClientPokemon extends Pokemon.
    // So `Object.getPrototypeOf(ClientPokemon.prototype)` is `Pokemon.prototype`.
    const pokemonProto = Object.getPrototypeOf(ClientPokemon.prototype);
    const fatal = pokemonProto.takeDamage.call(this, amount, skipAnimation);

    // Always show floating damage
    this.showFloatingDamage(amount);

    const WHITE_MATRIX: ColorMatrix = [
      // R'
      0, 0, 0, 0, 1,
      // G'
      0, 0, 0, 0, 1,
      // B'
      0, 0, 0, 0, 1,
      // A'  (keep alpha)
      0, 0, 0, 1, 0,
    ];

    // Mark as flashing to prevent concurrent flashes
    this.isFlashing = true;

    try {
      // Get base filters (excluding any existing white flash)
      const baseFilters = this.getBaseFilters();

      // If skipAnimation is true (DoT damage), just show a quick flash
      if (skipAnimation) {
        this.whiteFlash.matrix = WHITE_MATRIX;
        this.animatedSprite!.filters = [...baseFilters, this.whiteFlash];
        await this.sleep(50);
        this.animatedSprite!.filters = baseFilters;
        return fatal;
      }

      // Store current animation
      const animationBeforeHurt = this.currentAnimName;
      const frameBeforeHurt = this.animatedSprite.currentFrame;

      // Play random hit animation
      const randomHit = ["Hit1", "Hit2", "Hit3"][Math.floor(Math.random() * 3)] ?? "Hit1";
      this.setAnimationOnce([randomHit], this.currentDir);

      for (let i = 0; i < 2; i++) {
        this.whiteFlash.matrix = WHITE_MATRIX;
        this.animatedSprite!.filters = [...baseFilters, this.whiteFlash];
        await this.sleep(30);

        this.animatedSprite!.filters = baseFilters;
        await this.sleep(30);
      }

      // Do NOT force restore to Idle here, let setAnimationOnce handle it via onComplete
      // this.setAnimation(["Idle"], this.currentDir, 0);

      this.isHurting = false;
      this.events.emit("hurt", false);
    } finally {
      // Always restore the flashing flag and ensure filters are correct
      this.isFlashing = false;
      // Safety check: ensure filters are properly restored
      this.ensureFiltersRestored();
    }

    return fatal;
  }
}
